/**
 * The compliance log, and the security alerts derived from it.
 *
 * Replaces `AuditLog.GetAuditLogs` and `AuditLog.GetAuditLogMethods`, which
 * were served by `keyon/audit_log_retrieval.php` — an endpoint with no
 * authentication of any kind, reachable at its own URL, that took `account_id`
 * from the caller and interpolated the caller's `order_by` straight into the
 * statement. Reading the log now needs `audit:read`, the tenant is not an input
 * at all, sorting goes through an allow-list, and the read is itself audited.
 *
 * The system-alert reads are new: five paths in the legacy wrote `systemalerts`
 * and nothing exposed it, so the detectors ran and nobody could see the output.
 * Creating an alert is not here — that endpoint accepts service clients only,
 * because `CreateSystemAlert` was public and anyone could plant a false alert or
 * bury a real one.
 */

import api from './client';

// --- audit events ----------------------------------------------------------
//
// `?from=&to=` bound the window; `?filter[actor_id]`, `[subject_patient_id]`,
// `[subject_user_id]`, `[action]`, `[resource_type]`, `[resource_id]`,
// `[scope]`, `[endpoint]`, `[actor_name]`, `[patient_name]`,
// `[break_glass]`, `[ip]`, `[group_id]`, `[alert]`, `[alert_id]`,
// `[request_id]` and `[route_name]` narrow it.

export function listAuditEvents(params = {}) {
  return api.list('/audit-events', params);
}

/**
 * The distinct actions and resource types actually present, for the filter
 * pickers. Deliberately unaudited and PHI-free: it describes the log's shape,
 * not its contents. It replaces a hard-coded array of `API_Meth` strings the
 * SPA carried inline and which drifted from the server every release.
 */
export function getAuditEventFacets(params = {}) {
  return api.get('/audit-events/facets', params);
}

/**
 * `format` is `csv` (default) or `json`, which is NDJSON - one row per line,
 * the whole search, not one page. Throttled: this is the log's exit.
 */
export function exportAuditEvents(params = {}) {
  return api.download('/audit-events/export', params);
}

// --- security alerts -------------------------------------------------------

export function listSystemAlerts(params = {}) {
  return api.list('/system-alerts', params);
}

export function getSystemAlert(alertId) {
  return api.get(`/system-alerts/${alertId}`);
}

/**
 * The routine review (Trello bMZnxWqc): `status` is `acknowledged`,
 * `resolved` or `suppressed` (a false positive); closing one needs
 * `resolution_notes`. The reviewer and the time are the server's, and the
 * person an alert is about, or who caused it, is refused (403
 * `alert_review_by_party`).
 */
export function reviewSystemAlert(alertId, { status, resolutionNotes } = {}) {
  return api.patch(`/system-alerts/${alertId}`, {
    status,
    ...(resolutionNotes ? { resolution_notes: resolutionNotes } : {}),
  });
}

/**
 * The field changes behind one access row (Trello 6DoFcH1T): every changed
 * field by name, old and new values only for the fields their model declares
 * audited - and, for a patient's chart, only if the reader may enter that
 * chart: otherwise the change has `values_withheld` and no values. Reading
 * it is itself audited, against each patient it names.
 */
export async function listAuditEventChanges(eventId) {
  const response = await api.raw.get(`/audit-events/${encodeURIComponent(eventId)}/changes`);
  const body = response.data || {};

  return { changes: body.data || [], meta: body.meta || {} };
}

/**
 * What crossed the boundary (Trello NubOpoL5): Twilio, mail, Nursys, NPPES,
 * Fora and webhook exchanges by counterparty, outcome and SHA-256 - never a
 * payload. `filter[counterparty]`, `[direction]`, `[status]`,
 * `[request_id]`, `[subject_patient_id]` narrow it.
 */
export function listIntegrationExchanges(params = {}) {
  return api.list('/integration-exchanges', params);
}

export default {
  listAuditEvents, getAuditEventFacets, exportAuditEvents,
  listSystemAlerts, getSystemAlert, reviewSystemAlert, listAuditEventChanges,
  listIntegrationExchanges,
};
